import { lazy, Suspense, useMemo, memo, useState } from "react";
import { useHomepageDesign, getDesignFontStyles } from "@/hooks/useHomepageDesign";
import { useSiteContent } from "@/hooks/useSiteContent";
import { getDesignById } from "@/data/homepageDesigns";
import LazySection from "@/components/LazySection";
import { SortedSections } from "@/components/SortedSections";

import Design1 from "./Design1";

const retryImport = <T,>(factory: () => Promise<T>, retries = 3): Promise<T> =>
  factory().catch((error) => {
    if (retries <= 0) throw error;
    return new Promise<T>((resolve, reject) => {
      window.setTimeout(() => retryImport(factory, retries - 1).then(resolve).catch(reject), 600);
    });
  });

const lazyRetry = <P extends object = Record<string, never>>(
  factory: () => Promise<{ default: React.ComponentType<P> }>,
) => lazy(() => retryImport(factory));

const HomeCategorySections = lazyRetry<{ enabled?: boolean }>(() => import("@/components/HomeCategorySections"));

const Design2 = lazyRetry(() => import("./Design2"));
const Design3 = lazyRetry(() => import("./Design3"));
const Design4 = lazyRetry(() => import("./Design4"));
const Design5 = lazyRetry(() => import("./Design5"));
const Design6 = lazyRetry(() => import("./Design6"));
const Design7 = lazyRetry(() => import("./Design7"));
const Design8 = lazyRetry(() => import("./Design8"));
const Design9 = lazyRetry(() => import("./Design9"));
const Design10 = lazyRetry(() => import("./Design10"));
const Design11 = lazyRetry(() => import("./Design11"));
const Design12 = lazyRetry(() => import("./Design12"));
const Design13 = lazyRetry(() => import("./Design13"));
const Design14 = lazyRetry(() => import("./Design14"));
const Design15 = lazyRetry(() => import("./Design15"));
const Design16 = lazyRetry(() => import("./Design16"));
const Design17 = lazyRetry(() => import("./Design17"));
const Design18 = lazyRetry(() => import("./Design18"));
const Design19 = lazyRetry(() => import("./Design19"));
const Design20 = lazyRetry(() => import("./Design20"));
const Design21 = lazyRetry(() => import("./Design21"));
const Design22 = lazyRetry(() => import("./Design22"));
const Design23 = lazyRetry(() => import("./Design23"));
const Design24 = lazyRetry(() => import("./Design24"));
const Design25 = lazyRetry(() => import("./Design25"));
const Design26 = lazyRetry(() => import("./Design26"));
const Design27 = lazyRetry(() => import("./Design27"));
const Design28 = lazyRetry(() => import("./Design28"));
const Design29 = lazyRetry(() => import("./Design29"));
const Design30 = lazyRetry(() => import("./Design30"));
const Design31 = lazyRetry(() => import("./Design31"));
const Design32 = lazyRetry(() => import("./Design32"));
const Design33 = lazyRetry(() => import("./Design33"));
const Design34 = lazyRetry(() => import("./Design34"));
const Design35 = lazyRetry(() => import("./Design35"));
const Design36 = lazyRetry(() => import("./Design36"));
const Design37 = lazyRetry(() => import("./Design37"));
const Design38 = lazyRetry(() => import("./Design38"));
const Design39 = lazyRetry(() => import("./Design39"));
const Design40 = lazyRetry(() => import("./Design40"));
const Design41 = lazyRetry(() => import("./Design41"));
const Design42 = lazyRetry(() => import("./Design42"));
const Design43 = lazyRetry(() => import("./Design43"));
const Design44 = lazyRetry(() => import("./Design44"));
const Design45 = lazyRetry(() => import("./Design45"));
const Design46 = lazyRetry(() => import("./Design46"));
const Design47 = lazyRetry(() => import("./Design47"));
const Design48 = lazyRetry(() => import("./Design48"));
const Design49 = lazyRetry(() => import("./Design49"));
const Design50 = lazyRetry(() => import("./Design50"));
const Design51 = lazyRetry(() => import("./Design51"));
const Design52 = lazyRetry(() => import("./Design52"));

const lazyDesigns: Record<number, React.LazyExoticComponent<React.ComponentType>> = {
  2: Design2, 3: Design3, 4: Design4, 5: Design5, 6: Design6,
  7: Design7, 8: Design8, 9: Design9, 10: Design10,
  11: Design11, 12: Design12, 13: Design13, 14: Design14, 15: Design15,
  16: Design16, 17: Design17, 18: Design18, 19: Design19, 20: Design20,
  21: Design21, 22: Design22, 23: Design23, 24: Design24, 25: Design25,
  26: Design26, 27: Design27, 28: Design28, 29: Design29, 30: Design30,
  31: Design31, 32: Design32, 33: Design33, 34: Design34, 35: Design35,
  36: Design36, 37: Design37, 38: Design38, 39: Design39, 40: Design40,
  41: Design41, 42: Design42, 43: Design43, 44: Design44, 45: Design45,
  46: Design46, 47: Design47, 48: Design48, 49: Design49, 50: Design50,
  51: Design51, 52: Design52,
};

const SectionLoader = () => (
  <div className="py-20" style={{ minHeight: '400px' }}>
    <div className="container mx-auto px-4">
      <div className="text-center mb-8">
        <div className="h-3 w-24 mx-auto mb-3 rounded animate-pulse bg-muted" />
        <div className="h-8 w-48 mx-auto rounded animate-pulse bg-muted" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {[1,2,3,4].map(i => (
          <div key={i} className="space-y-3">
            <div className="rounded-lg animate-pulse bg-muted" style={{ aspectRatio: "3/4" }} />
            <div className="h-4 w-3/4 rounded animate-pulse bg-muted" />
            <div className="h-4 w-1/2 rounded animate-pulse bg-muted" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

interface Props {
  previewDesignId?: number | null;
}

const HomepageRenderer = memo(({ previewDesignId }: Props) => {
  const { design, designId, loading } = useHomepageDesign();
  const { content, loading: contentLoading } = useSiteContent();
  const [homeSectionsVisible, setHomeSectionsVisible] = useState(false);

  const activeId = previewDesignId || designId;
  const activeDesign = useMemo(
    () => (previewDesignId ? getDesignById(previewDesignId) : design),
    [previewDesignId, design]
  );

  const fontOverrides = {
    heading: content.font_heading || undefined,
    ui: content.font_ui || undefined,
    body: content.font_body || undefined,
  };

  const fontStyles = useMemo(
    () => getDesignFontStyles(activeDesign, fontOverrides),
    [activeDesign, fontOverrides.heading, fontOverrides.ui, fontOverrides.body]
  );

  // Render Design1 immediately while loading — eliminates blank skeleton gate that was killing LCP
  const isLoading = !previewDesignId && (loading || contentLoading);
  const effectiveId = isLoading ? (activeId || 1) : activeId;

  if (effectiveId === 1) {
    return (
      <div style={fontStyles as any} className="homepage-design">
        <SortedSections>
          <Design1 />
          <LazySection height="600px" rootMargin="0px" onVisible={() => setHomeSectionsVisible(true)}>
            <Suspense fallback={null}>
              <HomeCategorySections enabled={homeSectionsVisible} />
            </Suspense>
          </LazySection>
        </SortedSections>
      </div>
    );
  }

  const LazyComponent = lazyDesigns[effectiveId];
  if (!LazyComponent) {
    return (
      <div style={fontStyles as any} className="homepage-design">
        <SortedSections>
          <Design1 />
          <LazySection height="600px" rootMargin="0px" onVisible={() => setHomeSectionsVisible(true)}>
            <Suspense fallback={null}>
              <HomeCategorySections enabled={homeSectionsVisible} />
            </Suspense>
          </LazySection>
        </SortedSections>
      </div>
    );
  }

  return (
    <div style={fontStyles as any} className="homepage-design">
      <SortedSections>
        <Suspense fallback={<SectionLoader />}>
          <LazyComponent />
        </Suspense>
        <LazySection height="600px" rootMargin="0px" onVisible={() => setHomeSectionsVisible(true)}>
          <Suspense fallback={null}>
            <HomeCategorySections enabled={homeSectionsVisible} />
          </Suspense>
        </LazySection>
      </SortedSections>
    </div>
  );
});

HomepageRenderer.displayName = "HomepageRenderer";

export default HomepageRenderer;
